/**
 * THE FOUR QUESTIONS THE INSIGHTS SCREEN ANSWERS — #303.
 *
 * WHY THIS EXISTS. The screen was six full-width panels stacked down the page,
 * five of them bar charts, in an order nobody chose. Vinnie, 27 August 2026:
 * *"other than those numbers this is highly disorganized. I cannot scroll past
 * What's stuck and then go to another bar and then another bar and then the
 * epic. I wanted something that makes it easy to get insights of the work that
 * is currently being done."*
 *
 * A DASHBOARD IS ONE SCREEN. Stephen Few's definition is information
 * "consolidated and arranged on a single screen so the information can be
 * monitored at a glance", and the reason is memory rather than taste: short-term
 * memory holds around eight things, so anything you scroll back for you have to
 * carry in your head. Six panels down a page is a report.
 *
 * So there are four answers, not six panels, and the four breakdowns collapse
 * into ONE chart with a picker:
 *
 *   IN FLIGHT      what is being worked on right now, and what has stalled
 *   IN YOUR WAY    what is stuck, grouped by the thing being waited on
 *   KEEPING UP     is the backlog growing or shrinking
 *   WHERE THE WORK IS   one chart, split by area, epic, type or stage
 *
 * The maths lives here rather than in the component so it can be checked
 * without a browser — see scripts/test-insights.ts.
 */
import { ORIGINAL_TYPES, labelOf, meansOf, type IssueType } from './issue-types';
import type { Area, Issue, IssueStatus } from './types';
import { STATUS_LABELS, STATUS_ORDER } from './types';
import { dueSlip, isLiveCard, PUSH_MARK_THRESHOLD } from './card-slip';
import { NO_AREA } from './areas-report';
import { displayName, type Profiles } from './people';
import { ageInDays } from './card-age';
import { calendarIsActive, workingHolidayInstants, pushPastHolidays, closesBetween, type WorkCalendar, type Holiday } from './work-calendar';

const DAY = 24 * 60 * 60 * 1000;
const isClosed = (i: Issue) => i.status === 'done' || i.status === 'wontdo';

/**
 * A NAMED COUNT INSIDE A BAR — what every hover card on this screen is made of.
 *
 * Three charts, one shape. Vinnie, 28 August 2026, asked for a card on all
 * three; writing "8 in review" three different ways in three components is how
 * the same fact ends up phrased three ways and read as three facts.
 */
export interface Part {
  label: string;
  n: number;
}

/** Which stages a set of work is in, in workflow order, empties dropped. */
export function byStage(issues: Issue[]): Part[] {
  return STATUS_ORDER.map((st) => ({
    label: STATUS_LABELS[st] ?? st,
    n: issues.filter((i) => i.status === st).length,
  })).filter((p) => p.n > 0);
}

/**
 * WHO IS HOLDING A SET, biggest first, with nobody last however big it is.
 *
 * "Nobody has picked these up" is the most useful thing a hover card can say
 * about a bar that is too tall, so it is an entry rather than a gap — and last,
 * for the same reason "No area" is last in the chart underneath it.
 */
export function byPerson(issues: Issue[], profiles: Profiles = {}): Part[] {
  const n = new Map<string, number>();
  for (const i of issues) n.set((i.assignee ?? '').trim(), (n.get((i.assignee ?? '').trim()) ?? 0) + 1);
  return [...n.entries()]
    .map(([who, count]) => ({ label: who ? displayName(who, profiles) : 'Nobody', n: count, empty: !who }))
    .sort((a, b) => Number(a.empty) - Number(b.empty) || b.n - a.n || a.label.localeCompare(b.label))
    .map(({ label, n: count }) => ({ label, n: count }));
}

export interface FlightRow {
  issue: Issue;
  /** Whole days since anything last happened to it. */
  days: number;
  /** Sitting longer than the rest of its own stage on this board. */
  stalled: boolean;
}

export interface FlightBucket {
  /** What goes under the bar on the axis. */
  label: string;
  count: number;
  /**
   * How many of them have sat longer than the average for their own stage.
   *
   * A PART OF THE BAR RATHER THAN A COLOUR FOR THE WHOLE OF IT. Colouring a
   * bucket only when everything in it was stalled was the first attempt, and on
   * a real board it lit one small bar while the panel's own first line said
   * fourteen were sitting too long — the picture and the sentence disagreed.
   * Two stages with different paces put a stalled card and a healthy one in the
   * same bucket all the time, so the split has to be shown inside it.
   */
  stalled: number;
  /* WHAT THE HOVER CARD SAYS. A bar already says how many and how many are
     late; a card that repeats that is the number again, bigger. These are the
     two things the chart cannot show: what the bucket is made of, and whose
     desk it is on. */
  inprogress: number;
  staging: number;
  inreview: number;
  who: Part[];
  /** The cards themselves, so the bucket can open exactly them. */
  ids: string[];
}

export interface Flight {
  inprogress: FlightRow[];
  /** Only populated on boards with the In Staging column turned on; [] elsewhere. */
  staging: FlightRow[];
  inreview: FlightRow[];
  total: number;
  stalled: number;
  /** How long things have been sitting, as a shape rather than as a list. */
  buckets: FlightBucket[];
  /** The stalled ones, oldest first — the only rows worth putting on a screen. */
  longest: FlightRow[];
}

/*
 * THE BOUNDARIES ARE AN AXIS, NOT A VERDICT.
 *
 * Nothing above says a card in "6–13d" is late; a week is a week on every
 * board, and these are only the marks a person already thinks in. The judgement
 * — the amber — stays relative to this board, as `stalled` always was. That
 * distinction is the whole reason a fixed set of edges is allowed here at all.
 */
const FLIGHT_BUCKETS: { label: string; upTo: number }[] = [
  { label: 'today', upTo: 0 },
  { label: '1–5d', upTo: 5 },
  { label: '6–13d', upTo: 13 },
  { label: '2w+', upTo: Infinity },
];

/**
 * WHAT IS ACTUALLY MOVING, and which of it has stopped moving.
 *
 * NOT A LIST OF EVERYTHING IN FLIGHT. Vinnie, 28 August 2026, asked whether
 * this had to be a list at all. On his board it was thirty-two rows, which is
 * the backlog with two filters on it — an inventory, not an insight, and tall
 * enough to strand the panel beside it. So: the SHAPE of how long things have
 * been sitting, and then only the ones sitting too long. The healthy majority
 * is a number. What is returned is a fixed height whatever the board is doing.
 *
 * STALLED IS RELATIVE TO THIS BOARD, NOT TO A NUMBER I PICKED. A board that
 * ships daily and a board that ships quarterly disagree about what "too long"
 * means, and a fixed threshold is wrong on one of them by definition. So a row
 * is stalled when it has sat longer than the average for its own stage.
 *
 * Below three items in a stage there is no average worth having — one item is
 * always exactly average, and two make whichever is older "stalled" whatever
 * the gap. So nothing is flagged until there are three, which reads as "no
 * opinion yet" rather than as a false alarm.
 */
export function inFlight(issues: Issue[], now: number, profiles: Profiles = {}): Flight {
  const out: Flight = {
    inprogress: [],
    staging: [],
    inreview: [],
    total: 0,
    stalled: 0,
    buckets: FLIGHT_BUCKETS.map((b) => ({
      label: b.label, count: 0, stalled: 0, inprogress: 0, staging: 0, inreview: 0, who: [], ids: [],
    })),
    longest: [],
  };

  for (const stage of ['inprogress', 'staging', 'inreview'] as const) {
    const mine = issues.filter((i) => i.status === stage);
    // Age from when the card ENTERED its working column (`startedAt`, clamped
    // server-side in 0033) — the same clock the board's own age marker uses
    // (card-age.ts), so the two surfaces never disagree. NOT `updatedAt`, which
    // any edit resets: a card touched today would falsely read "0 days in flight",
    // and an imported card read its import date. A card with no recorded start
    // cannot be aged (null); it still counts as in flight but is never stalled.
    const knownAge = (i: Issue) => ageInDays(i.startedAt, now);
    const known = mine.map(knownAge).filter((d): d is number => d !== null);
    const mean = known.length ? known.reduce((a, d) => a + d, 0) / known.length : 0;
    out[stage] = mine
      .map((i) => {
        const d = knownAge(i);
        return { issue: i, days: d ?? 0, stalled: d !== null && known.length >= 3 && d > mean };
      })
      .sort((a, b) => b.days - a.days);
  }

  const all = [...out.inprogress, ...out.staging, ...out.inreview];
  out.total = all.length;
  out.longest = all.filter((r) => r.stalled).sort((a, b) => b.days - a.days);
  out.stalled = out.longest.length;

  /* Held per bucket while filling, so `who` can be worked out from the real
     issues rather than from a running tally that could not name anybody. */
  const held: Issue[][] = out.buckets.map(() => []);
  for (const r of all) {
    const at = FLIGHT_BUCKETS.findIndex((x) => r.days <= x.upTo);
    const b = out.buckets[at];
    b.count++;
    if (r.stalled) b.stalled++;
    if (r.issue.status === 'inreview') b.inreview++;
    else if (r.issue.status === 'staging') b.staging++;
    else b.inprogress++;
    b.ids.push(r.issue.id);
    held[at].push(r.issue);
  }
  out.buckets.forEach((b, at) => { b.who = byPerson(held[at], profiles); });

  return out;
}

export interface KeepingUp {
  /** Days the window covers, so the screen can say which window it means. */
  days: number;
  finished: number;
  raised: number;
  shipped: number;
  /** Raised minus finished. Positive means the backlog grew. */
  growth: number;
  open: number;
  /**
   * Weeks to clear what is open at the rate of the window, or null when the
   * rate is zero — "infinity weeks" is not a thing to put on a screen, and
   * "0 weeks" would be the opposite of the truth.
   */
  weeksToClear: number | null;
  /* WHAT THE HOVER CARDS SAY. A bar of 52 does not say how fast that is, and
     "about 13 a week" is the sentence somebody actually repeats out loud. */
  perWeek: { finished: number; raised: number; shipped: number };
  finishedIds: string[];
  raisedIds: string[];
}

export function keepingUp(issues: Issue[], buildDates: string[], now: number, days = 28, paceWindowDays = days): KeepingUp {
  const from = now - days * DAY;
  // Count a card as finished in the window by WHEN IT FINISHED (`completedAt`,
  // stamped in 0096), not `updatedAt` — a later comment or edit would otherwise
  // drag a card into the wrong window, or out of it. Cards finished before 0096
  // carry no completedAt; they fall back to `updatedAt` so legacy throughput is
  // not silently lost.
  const doneAt = (i: Issue) => i.completedAt ?? i.updatedAt ?? 0;
  const done = issues.filter((i) => i.status === 'done' && doneAt(i) >= from);
  const made = issues.filter((i) => (i.createdAt ?? 0) >= from);
  const finished = done.length;
  const raised = made.length;
  const shipped = buildDates.filter((d) => d && Date.parse(d) >= from).length;
  // Open = committed work still to clear. The backlog is candidate work (the
  // holding pen), so it never counts toward "open" or the weeks-to-clear read —
  // the aggregate-bucket invariant. (The Forecast-Finish SCOPE is mode-keyed
  // separately; this keeping-up read is committed-only in every mode.)
  const open = issues.filter((i) => !isClosed(i) && i.status !== 'backlog').length;
  const weeks = days / 7;
  /* One decimal, because a rate of 0.4 a week rounded to 0 says "nothing is
     happening" about a board where something is. */
  const rate = (n: number) => Math.round((n / weeks) * 10) / 10;
  // THE FINISH-RATE that weeksToClear projects from reads the PACE window, not the display
  // window (FF-1): the "N open take about W weeks" conclusion must agree with the finish date
  // and the go-live odds, which read the canonical pace window. By default the two windows are
  // the same (back-compatible); Insights passes the 12-week pace window so its finish projection
  // can never point opposite the forecast while the counts above stay on the chosen window.
  const paceFrom = now - paceWindowDays * DAY;
  const paceFinished = issues.filter((i) => i.status === 'done' && doneAt(i) >= paceFrom).length;
  const pacePerWeek = paceFinished / (paceWindowDays / 7);
  return {
    days,
    finished,
    raised,
    shipped,
    growth: raised - finished,
    open,
    weeksToClear: pacePerWeek > 0 ? Math.ceil(open / pacePerWeek) : null,
    perWeek: { finished: rate(finished), raised: rate(raised), shipped: rate(shipped) },
    finishedIds: done.map((i) => i.id),
    raisedIds: made.map((i) => i.id),
  };
}

/* ── FLOW HEALTH: is the work moving, and how fast ────────────────────────── */

export interface ThroughputWeek {
  /** Start of the week, epoch ms — so a hover can name it. */
  from: number;
  /** Cards finished that week. */
  n: number;
}

export interface Throughput {
  weeks: number;
  /** One point per week, OLDEST FIRST — the shape the sparkline is drawn from. */
  series: ThroughputWeek[];
  /** Mean finished per week over the window, to one decimal. */
  perWeek: number;
  /**
   * Whether the recent half of the window is finishing faster, slower, or at the
   * same pace as the older half. A rate is a number; a person wants to know if it
   * is going the right way, which a single number cannot say.
   */
  trend: 'steady' | 'speeding' | 'slowing';
}

/**
 * CARDS FINISHED PER WEEK, AS A SHAPE — the throughput card on Insights.
 *
 * A rate on its own ("6 a week") hides the thing worth seeing: a run of empty
 * weeks, or a burst after one. So this returns a week-by-week series for the
 * sparkline as well as the mean, and classifies the drift so the card can say
 * "no slowdown" rather than leave the reader to eyeball it.
 *
 * Finished is counted by `completedAt` (0096), falling back to `updatedAt` for
 * cards closed before that field existed — the same clock `keepingUp` uses, so
 * the two cards never disagree about what finished when.
 */
/**
 * `sinceMs` (the board's first card `createdAt`, 0217) clamps the window to the
 * board's own life. A 12-week window on a board that is six weeks old reaches back
 * before the first card existed and counts those weeks as "nothing finished" — but
 * they are not quiet weeks (a real slowdown, which we keep), they are NO BOARD YET,
 * and feeding them to the forecast biases a young board's pace pessimistic and makes
 * its "last 12 weeks' pace" caption claim history it never had. A bucket entirely
 * before the first card can hold no completion (completedAt ≥ createdAt ≥ sinceMs),
 * so the trim drops only genuinely-empty leading weeks — no real finish is lost.
 * Omitted (the Throughput panel), the window is the full `weeks` as before.
 */
export function throughput(issues: Issue[], now: number, weeks = 10, sinceMs?: number): Throughput {
  const doneAt = (i: Issue) => i.completedAt ?? i.updatedAt ?? 0;
  const done = issues.filter((i) => i.status === 'done');
  const full: ThroughputWeek[] = [];
  for (let w = weeks - 1; w >= 0; w--) {
    const from = now - (w + 1) * 7 * DAY;
    const to = now - w * 7 * DAY;
    full.push({ from, n: done.filter((i) => { const t = doneAt(i); return t >= from && t < to; }).length });
  }
  /* Keep the last `eff` whole weeks — the board's age when it is younger than the
     window, the full window otherwise. At least one week so a day-old board forecasts. */
  const eff =
    sinceMs != null && Number.isFinite(sinceMs)
      ? Math.max(1, Math.min(weeks, Math.ceil((now - sinceMs) / (7 * DAY))))
      : weeks;
  const series = full.slice(weeks - eff);
  const total = series.reduce((a, s) => a + s.n, 0);
  const perWeek = Math.round((total / eff) * 10) / 10;
  /* A drift of under one card a week is a normal week, not a trend — the halves
     are compared by their means so an uneven window (11 weeks) is not skewed. */
  const half = Math.max(1, Math.floor(eff / 2));
  const meanOf = (xs: ThroughputWeek[]) => xs.reduce((a, s) => a + s.n, 0) / Math.max(1, xs.length);
  const older = meanOf(series.slice(0, half));
  const recent = meanOf(series.slice(eff - half));
  const trend = recent - older >= 1 ? 'speeding' : older - recent >= 1 ? 'slowing' : 'steady';
  return { weeks: eff, series, perWeek, trend };
}

/* ── FORECAST FINISH: a date with a confidence, from the board's own pace ──── */

export type Rag = 'good' | 'warn' | 'crit';

/** One point on the cumulative S-curve — the chance the remaining work is all
 *  finished by this date. Oldest date first. */
export interface ForecastPoint {
  /** A date on the X-axis, epoch ms. */
  at: number;
  /** Cumulative chance everything is done by then, 0–1. */
  p: number;
}

/** One bar of the likelihood spread — the chance the work finishes INSIDE this
 *  date window (not by it). The windows are equal and adjacent, so their chances
 *  sum to the mass on the axis. This is likelihood, never a run count. */
export interface ForecastBin {
  /** The window's start, epoch ms (inclusive). */
  from: number;
  /** The window's end, epoch ms (exclusive). */
  to: number;
  /** Chance the work finishes in this window, 0–1. */
  p: number;
}

export interface Forecast {
  /**
   * Whether there is enough finished history to simulate a spread that would not
   * mislead. Below the threshold the panel shows its cold-start state instead of
   * a confident curve, and the fields below are the diagnostics that state reads.
   */
  ready: boolean;
  /** Finished cards with a recorded completion inside the throughput window. */
  finished: number;
  /** How many of the window's weeks actually saw a card finish — the "across N
   *  weeks" half of the cold-start test. */
  weeksWithFinishes: number;

  /* ── Present whether ready or not, so the summary can always read them ── */
  /** Open, non-abandoned cards still to finish — the scope the run consumes. */
  remaining: number;
  /** Mean cards finished per week over the window, to one decimal. */
  perWeek: number;
  /** Whether the pace is holding, rising or falling (from throughput). */
  trend: 'steady' | 'speeding' | 'slowing';
  /** Weeks of throughput history the simulation resampled from. */
  weeks: number;
  /** How many trials were run. */
  trials: number;

  /* ── Only meaningful when ready; zero / empty otherwise ── */
  /** The early edge of the likely range — 10% of runs finish this soon. The band
   *  is drawn from here (its faint outer end) to p95. Epoch ms. */
  p10: number;
  /** The date half the runs finish on or before — the coin-flip. Epoch ms. */
  p50: number;
  /** The 85%-confidence date — the honest headline. Epoch ms. */
  p85: number;
  /** The near-certain date. Epoch ms. */
  p95: number;
  /** The cumulative distribution, oldest date first — chance of finishing BY each
   *  date. The confidence reader interpolates this to answer any date the user
   *  points at; the band is drawn from its quantiles. */
  curve: ForecastPoint[];
  /** The likelihood SPREAD — equal date windows across the same axis as the band,
   *  each holding the chance the work finishes IN that window (not by it). This is
   *  the drill-down histogram: pure likelihood per window, summing to ~1 across the
   *  axis, never a count of runs. Empty until ready. */
  spread: ForecastBin[];

  /* ── The working calendar's effect, only when one is set (0216) ── */
  /** Whether a known future holiday or shutdown pushed the headline (p85) date
   *  later than the pure-cadence resample would have landed it. The foot-cue shows
   *  ONLY when this is true — a holiday the forecast actually had to step over
   *  (Vinnie, 28 Oct). False or absent on the default calendar, or when the closes
   *  all fall after the work is already done. */
  holidayMoved?: boolean;
  /** How many calendar days the headline moved by, when it did — for the cue's
   *  wording ("a 2-week close pushes this out ~9 days"). */
  holidayDays?: number;
  /** The workspace closes the forecast stepped over to reach the headline — so the
   *  cue can NAME them (one → named and dated, several → counted). Present only when
   *  `holidayMoved`. Raw entries, not display text: the panel formats them. */
  holidayCloses?: Holiday[];

  /* ── The go-live verdict, only when a target date is set (decision #1) ── */
  /** The project's go-live target, epoch ms — absent when none is set. */
  target?: number;
  /** Chance of finishing on or before the target, 0–1. */
  targetProb?: number;
  /** good ≥ 85%, warn 50–85%, crit < 50% — the colour AND the word. */
  rag?: Rag;
}

/**
 * ENOUGH FINISHED WORK TO FORECAST — Vinnie's locked threshold (this session):
 * at least eight finished cards spread across at least four weeks, each with a
 * recorded completion. Below either bar the spread a simulation would draw is
 * noise, so the panel says so rather than drawing a confident, wrong curve.
 */
export const FORECAST_MIN_FINISHED = 8;
export const FORECAST_MIN_WEEKS = 4;
const FORECAST_TRIALS = 10000;
/* A trial that keeps drawing empty weeks would never consume the scope; ten
   years of weeks is "never" on any real board and stops the loop dead. */
const FORECAST_MAX_WEEKS = 520;

/**
 * HOW MUCH MORE A RECENT WEEK COUNTS — the one recency knob (#434 add-on).
 *
 * Plain bootstrap treats a week from three months ago exactly like last week,
 * which lags a team that has sped up or slowed down. So each observed week is
 * resampled with a weight that decays exponentially with its age: a week this
 * many weeks older than the most recent one is worth half as much. The half-life
 * is half the history window — recent enough to track a real change in pace,
 * long enough that one fluke week cannot run the forecast on its own.
 *
 * ONE KNOB ON PURPOSE. A research pass (recorded in the #434 report) found plain
 * resampling beats sequence-aware ML on this problem; recency weighting is the
 * single cheap, explainable accuracy win. The method a PM reads stays "resample
 * your observed weekly cadence, counting recent weeks more" — no second
 * heuristic, no per-week hand-tuning. Set to Infinity to recover equal weights.
 */
const FORECAST_HALF_LIFE_FRACTION = 0.5;

/** The verdict a crossing chance earns — the same cut on the target line, the
 *  chip, the flag and the headline word, so they can never disagree. */
export const ragForProb = (p: number): Rag => (p >= 0.85 ? 'good' : p >= 0.5 ? 'warn' : 'crit');

/** A tiny deterministic PRNG (mulberry32) so a forecast is reproducible from its
 *  seed — the headline date must not move between reloads when nothing changed. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** One 32-bit seed for a forecast, folded (FNV-1a) from everything that should
 *  change the result: the board key, the remaining scope, the window, the target,
 *  and the throughput snapshot. Same data → same seed → same forecast; a genuine
 *  change in any of them reseeds and moves the date. */
function forecastSeed(
  key: string, counts: number[], remaining: number, weeks: number, target: number | null | undefined
): number {
  let h = 2166136261 >>> 0;
  const mix = (n: number) => { h = Math.imul(h ^ (n >>> 0), 16777619) >>> 0; };
  for (let i = 0; i < key.length; i++) mix(key.charCodeAt(i));
  for (const c of counts) mix(c);
  mix(remaining);
  mix(weeks);
  const tgt = Math.floor(target ?? 0);
  mix(tgt & 0xffffffff);
  mix(Math.floor(tgt / 0x100000000));
  return h >>> 0;
}

/**
 * WHEN THE REMAINING WORK IS LIKELY TO BE DONE — a bootstrap Monte Carlo over the
 * board's own weekly throughput (#434).
 *
 * NO NEW INPUTS. It reuses throughput() — cards finished per week, read from
 * `completedAt` — and the open, non-abandoned scope, both of which FlightDeck
 * already holds. Each of ~10,000 trials resamples whole weeks of that history
 * (with replacement) until the scope is consumed, and records the finish date;
 * sorting those dates gives the spread the curve and the percentiles are read
 * from. The finish inside the last week is interpolated so the curve is smooth
 * rather than stepping a week at a time.
 *
 * RECENT WEEKS COUNT MORE. The resample is not uniform: each observed week is
 * weighted by how recent it is (see FORECAST_HALF_LIFE_FRACTION), so a team that
 * has sped up or slowed down is tracked rather than averaged flat across the
 * whole window. It stays one explainable knob — "resample your weekly cadence,
 * counting recent weeks more".
 *
 * The headline is the 85th percentile — an honest date, not a single false-
 * precision day. When a go-live target is set (the project's `goLive`, decision
 * #1) the verdict is P(finish ≤ target): the same number as where the target
 * line crosses the curve. With no target the curve and the confidence dates
 * still stand; the verdict and target line are simply omitted.
 *
 * DETERMINISTIC BY DEFAULT. With no `rng` passed, the PRNG is seeded from the
 * inputs themselves — the board key, the window, the remaining scope and the
 * throughput snapshot — so the same data always yields the same forecast and the
 * headline date never jitters between reloads, while it still moves the moment
 * the data genuinely changes. A false-precision wobble on a decision-tier date is
 * exactly what this panel promises not to do. `rng` stays injectable so a test
 * can drive the simulation with its own seed.
 */
export function forecast(
  issues: Issue[],
  now: number,
  target: number | null | undefined,
  weeks = 12,
  trials = FORECAST_TRIALS,
  rng?: () => number,
  seedKey = '',
  scope?: Issue[],
  cal?: WorkCalendar,
  countBacklog = true,
): Forecast {
  /* Clamp the pace window to the board's own life (0217): weeks before its first
     card existed are not slow weeks, they are no board, and counting them biases a
     young board pessimistic — see throughput(). firstSeen is the earliest card. */
  const firstSeen = issues.reduce((m, i) => (i.createdAt != null && i.createdAt < m ? i.createdAt : m), Infinity);
  const t = throughput(issues, now, weeks, firstSeen);
  const counts = t.series.map((s) => s.n);
  const finished = counts.reduce((a, n) => a + n, 0);
  const weeksWithFinishes = counts.filter((n) => n > 0).length;
  /* The CADENCE is the board's own throughput (above); the SCOPE it must consume
     is `scope` when a caller forecasts a checkpoint over only the work it gates,
     else the whole board. Board-wide pace × milestone-scoped remaining — the
     board is the well-sampled rate, the milestone is the work left (28 Oct).
     BACKLOG is mode-keyed (Vinnie, 30 Oct): a PROJECT counts its backlog as
     planned work still to do (`countBacklog` true — the default and the unset=project
     case), so a project with work parked in the backlog forecasts honestly and
     cannot read "nearly done"; a CONTINUOUS board's backlog is an open-ended
     candidate pool, so it leaves the remaining scope. Same rule feeds the
     completion % (Dashboard) so the two never disagree. */
  const remaining = (scope ?? issues).filter(
    (i) => !isClosed(i) && (countBacklog || i.status !== 'backlog'),
  ).length;

  const base: Forecast = {
    ready: false,
    finished,
    weeksWithFinishes,
    remaining,
    perWeek: t.perWeek,
    trend: t.trend,
    weeks: t.weeks,
    trials,
    p10: 0,
    p50: 0,
    p85: 0,
    p95: 0,
    curve: [],
    spread: [],
  };

  const enough = finished >= FORECAST_MIN_FINISHED && weeksWithFinishes >= FORECAST_MIN_WEEKS;
  /* Nothing left to finish is not a forecast — it is finished. And with no
     history there is nothing to resample. Both fall back to the cold state. */
  if (!enough || remaining === 0) return base;

  const n = counts.length;

  /* RECENCY WEIGHTS — counts is oldest-first, so week n-1 is the most recent and
     carries full weight; a week `age` weeks older is worth 0.5^(age/halfLife).
     Stored as a cumulative array so each draw is one binary search. Weeks with no
     finishes keep their weight: a recent quiet week is real evidence the pace has
     dropped, and dropping it would hide exactly the slowdown this is meant to see. */
  const halfLife = Math.max(1, n * FORECAST_HALF_LIFE_FRACTION);
  const cumW = new Array<number>(n);
  let acc = 0;
  for (let i = 0; i < n; i++) {
    const age = n - 1 - i;
    acc += Math.pow(0.5, age / halfLife);
    cumW[i] = acc;
  }
  const totalW = acc;
  /* Deterministic unless a test hands in its own RNG: same data → same forecast
     (no reload jitter), but a real change in the data reseeds and moves it. */
  const draw = rng ?? mulberry32(forecastSeed(seedKey, counts, remaining, weeks, target));
  /* The observed week a uniform draw lands in, under the recency weights. */
  const pickWeek = (): number => {
    const r = draw() * totalW;
    let lo = 0;
    let hi = n - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (cumW[mid] < r) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };

  const finishes = new Array<number>(trials);
  for (let k = 0; k < trials; k++) {
    let done = 0;
    let w = 0;
    let last = 0;
    while (done < remaining && w < FORECAST_MAX_WEEKS) {
      last = counts[pickWeek()];
      done += last;
      w++;
    }
    /* Place the finish INSIDE the final week: the last week's draw carried the
       count past the scope, and the fraction of it that was actually needed says
       how far into that week the work ran out. */
    let weeksToFinish = w;
    if (last > 0) {
      const overshoot = done - remaining; // ≥ 0
      weeksToFinish = w - 1 + (1 - overshoot / last);
    }
    finishes[k] = now + weeksToFinish * 7 * DAY;
  }

  /* THE WORKING CALENDAR, when one is set (0216). The resample runs at the board's
     weekly pace, which already holds its ORDINARY weekends — but a known FUTURE
     holiday or shutdown is not in that history, so each trial's finish is pushed out
     by the working-weekday holidays that fall before it. Precompute the holiday
     instants once over a horizon past the latest raw finish, then each trial is a few
     binary searches (pushPastHolidays), not a walk of the calendar. Weekends are never
     added — that would double-count the pace. Nothing happens on the default calendar. */
  /* Nearest-rank percentile — the same rule the rest of this file uses. Defined
     before the holiday push so the pre-push headline can be read with it too. */
  const at = (q: number): number => finishes[Math.min(trials - 1, Math.max(0, Math.ceil(q * trials) - 1))];

  let holidayMoved = false;
  let holidayDays = 0;
  if (cal && calendarIsActive(cal)) {
    let maxRaw = finishes[0];
    for (let k = 1; k < trials; k++) if (finishes[k] > maxRaw) maxRaw = finishes[k];
    const hols = workingHolidayInstants(now, maxRaw + 400 * DAY, cal);
    if (hols.length) {
      /* The headline the pure cadence would have landed on, before any close is
         stepped over — read from a sorted copy so the comparison is like-for-like
         (nearest-rank p85 vs nearest-rank p85). Cheap: one extra sort only when a
         future close actually exists. */
      const rawP85 = [...finishes].sort((a, b) => a - b)[Math.min(trials - 1, Math.max(0, Math.ceil(0.85 * trials) - 1))];
      for (let k = 0; k < trials; k++) finishes[k] = pushPastHolidays(now, finishes[k], hols);
      finishes.sort((a, b) => a - b);
      const movedBy = Math.round((at(0.85) - rawP85) / DAY);
      if (movedBy >= 1) { holidayMoved = true; holidayDays = movedBy; }
    } else {
      finishes.sort((a, b) => a - b);
    }
  } else {
    finishes.sort((a, b) => a - b);
  }

  const p10 = at(0.1);
  const p50 = at(0.5);
  const p85 = at(0.85);
  const p95 = at(0.95);

  /* The empirical distribution, by binary search over the sorted finishes. */
  const cdf = (x: number): number => {
    let lo = 0;
    let hi = trials;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (finishes[mid] <= x) lo = mid + 1;
      else hi = mid;
    }
    return lo / trials;
  };

  /* The X-domain spans the body of the distribution, always widened to hold the
     target so its crossing is on screen wherever it falls. Clamped to start no
     earlier than today — a "chance of finishing" before now is not a thing. */
  let lo = finishes[0];
  let hi = at(0.98);
  if (target != null) {
    lo = Math.min(lo, target);
    hi = Math.max(hi, target);
  }
  const pad = Math.max(DAY, (hi - lo) * 0.04);
  lo = Math.max(now, lo - pad);
  hi = hi + pad;

  const SAMPLES = 60;
  const curve: ForecastPoint[] = [];
  for (let s = 0; s <= SAMPLES; s++) {
    const x = lo + ((hi - lo) * s) / SAMPLES;
    curve.push({ at: x, p: cdf(x) });
  }

  /* THE LIKELIHOOD SPREAD — the same axis as the band and the reader, cut into
     equal date windows. Each window's height is the chance the work finishes IN
     it: cdf(end) − cdf(start). By construction they are adjacent and equal-width,
     so they sum to the mass on the axis and read honestly as "the chance it lands
     in this window", never a tally of runs. This is the drill-down histogram. */
  const BINS = 14;
  const spread: ForecastBin[] = [];
  for (let b = 0; b < BINS; b++) {
    const from = lo + ((hi - lo) * b) / BINS;
    const to = lo + ((hi - lo) * (b + 1)) / BINS;
    spread.push({ from, to, p: Math.max(0, cdf(to) - cdf(from)) });
  }

  const out: Forecast = { ...base, ready: true, p10, p50, p85, p95, curve, spread };
  if (holidayMoved) {
    out.holidayMoved = true;
    out.holidayDays = holidayDays;
    out.holidayCloses = closesBetween(now, p85, cal as WorkCalendar);
  }
  if (target != null) {
    const targetProb = cdf(target);
    out.target = target;
    out.targetProb = targetProb;
    out.rag = ragForProb(targetProb);
  }
  return out;
}

/**
 * THE SAME FORECAST, FOR A SCOPED CHECKPOINT — a milestone predicts over only the
 * work it gates, not the whole board. The CADENCE (how fast cards finish) is still
 * the BOARD's own throughput — the honest, well-sampled pace — while the SCOPE the
 * run must consume is just this milestone's open cards (Vinnie, 28 Oct). A go-live
 * ties to the whole board, so forecastScoped(board, board, …) === forecast(board, …).
 * Pass a milestone-specific `seedKey` so two checkpoints on one board never share a
 * seed (their scope/target usually differ and reseed anyway, but the key makes it
 * explicit). Below the cold-start gate it returns the same not-ready shape.
 */
export function forecastScoped(
  boardIssues: Issue[],
  scopeIssues: Issue[],
  now: number,
  target: number | null | undefined,
  weeks = 12,
  trials = FORECAST_TRIALS,
  rng?: () => number,
  seedKey = '',
  cal?: WorkCalendar,
  countBacklog = true,
): Forecast {
  return forecast(boardIssues, now, target, weeks, trials, rng, seedKey, scopeIssues, cal, countBacklog);
}

export interface CycleTime {
  /** Days the window covers, so the card can name it. */
  days: number;
  /** Cards finished in the window with BOTH a start and a finish — the sample. */
  sample: number;
  /** Median days from start to done. Half finish inside this. */
  median: number;
  /** 90th percentile — "the slowest tenth take this or more". */
  p90: number;
  /** Median over the window before this one, or null when too few to compare. */
  prevMedian: number | null;
  /** median − prevMedian (negative means finishing faster now), or null. */
  delta: number | null;
}

const DEFAULT_CYCLE_WINDOW = 28;
/* Below this many finished cards a median is noise, so the previous window is
   not offered as a comparison rather than swinging on one slow card. */
const CYCLE_MIN_SAMPLE = 4;

const median = (xs: number[]): number => {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

/** Nearest-rank quantile — the same rule card-age uses for the board norm. */
const quantile = (xs: number[], q: number): number => {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const idx = Math.min(s.length - 1, Math.ceil(q * s.length) - 1);
  return s[Math.max(0, idx)];
};

/* A card counts toward cycle time only if it spent a MEASURABLE span being
   worked. Since startedAt now stamps the moment a card leaves the queue for
   anywhere (whenWorkBegan), a card pulled straight from To-do to Done in one
   move has its start and finish written by the SAME operation — the browser
   stamps startedAt and the server stamps completedAt in that one write, so they
   land milliseconds apart, bar clock skew between the two. That is an instant
   landing, not a cycle: timing it as ~0 days would drag the median toward zero
   on a board that closes many cards the day they are picked up. These cards are
   not lost — lead time still counts them, because the wait before pickup
   (createdAt → completedAt) is their real story. The floor sits well above
   plausible clock skew (seconds) and below any genuine work session, so only
   same-move landings are excluded. Vinnie, 2 Nov 2026: a direct To-do→Done move
   "isn't shown as a literal 0-day cycle." */
const MIN_WORKED_MS = 5 * 60 * 1000; // five minutes
const hadWorkPhase = (i: Issue): boolean =>
  i.startedAt != null && i.completedAt != null && i.completedAt - i.startedAt >= MIN_WORKED_MS;

/**
 * HOW LONG A CARD TAKES START-TO-DONE — the cycle-time card on Insights.
 *
 * Measured from `startedAt` (0033) to `completedAt` (0097): the time a card
 * actually spent being worked, not the time since it was raised. A card missing
 * either stamp, or one picked straight to Done in a single move (no work phase —
 * see hadWorkPhase), cannot be timed and is left out of the sample rather than
 * counted as zero — the same honesty as the aging hero, which never ages a card
 * with no recorded start.
 *
 * The median answers "how long does a typical card take", the 90th percentile
 * answers "and the slow ones", and the previous window gives the median
 * something to be faster or slower THAN — a single number cannot say which way
 * the board is heading.
 */
export function cycleTime(issues: Issue[], now: number, days = DEFAULT_CYCLE_WINDOW): CycleTime {
  const from = now - days * DAY;
  const prevFrom = now - 2 * days * DAY;
  const span = (i: Issue): number | null =>
    hadWorkPhase(i) ? (i.completedAt! - i.startedAt!) / DAY : null;
  const finishedIn = (lo: number, hi: number): number[] =>
    issues
      .filter((i) => i.status === 'done' && (i.completedAt ?? 0) >= lo && (i.completedAt ?? 0) < hi)
      .map(span)
      .filter((d): d is number => d !== null);
  const cur = finishedIn(from, now + DAY);
  const prev = finishedIn(prevFrom, from);
  const med = Math.round(median(cur));
  const prevMed = prev.length >= CYCLE_MIN_SAMPLE ? Math.round(median(prev)) : null;
  return {
    days,
    sample: cur.length,
    median: med,
    p90: Math.round(quantile(cur, 0.9)),
    prevMedian: prevMed,
    delta: prevMed !== null ? med - prevMed : null,
  };
}

export interface LeadMonth {
  /** First millisecond of the calendar month, for the axis label. */
  from: number;
  /** Median lead days for cards finished in that month, 0 when none. */
  median: number;
  /** Cards finished that month with both a start and a finish — the sample. */
  sample: number;
}

export interface LeadTime {
  /** Days the window covers, so the card can name it. */
  days: number;
  /** Cards finished in the window with a start AND a finish — the sample. */
  sample: number;
  /** Median days RAISED to done (createdAt → completedAt). The hero. */
  median: number;
  /** The wait in the backlog, as the grey segment: lead median − work median, so
   *  Waiting + Working sum to the hero. The remainder of the lead time after the
   *  worked slice, not the separately-measured raise→start median (medians don't
   *  sum, so that would leave the bar short of the hero). */
  waitMedian: number;
  /** 90th percentile of the MEASURED raise→start wait — "the longest tenth of
   *  waits run this or more". A true tail number for the note, independent of the
   *  decomposed waitMedian above. */
  waitP90: number;
  /** Median days STARTED to done — the same number Cycle time shows. The green. */
  workMedian: number;
  /** Median lead over the window before this one, or null when too few. */
  prevMedian: number | null;
  /** median − prevMedian (negative means shorter waits now), or null. */
  delta: number | null;
  /** Lead median per calendar month, oldest first — the Insights month-trend. */
  months: LeadMonth[];
}

/* How many calendar months the Insights month-trend sparkline draws. */
const LEAD_TREND_MONTHS = 6;

/**
 * HOW LONG A CARD TAKES RAISED-TO-DONE, AND HOW MUCH OF IT IS THE WAIT — the
 * lead-time card on Insights, and its split-bar tile on the board metrics strip
 * (#439, split from cycle time #234).
 *
 * Cycle time answers "once we start, how long"; lead time answers "from being
 * asked to being delivered, how long" — and the gap between them is the time a
 * card sat in the backlog before anyone picked it up, usually the biggest and
 * least-visible number. All three are DERIVED from timestamps already captured:
 * lead is createdAt → completedAt, the wait is createdAt → startedAt, and the
 * worked slice is startedAt → completedAt — the exact span cycleTime() measures,
 * so the green segment locks to the Cycle time panel (both exclude an instant
 * To-do→Done landing, which had no work phase). A card missing a start is left
 * out of the sample, the same honesty as cycle time; a card that landed in one
 * move still counts in the lead and wait here, because its wait was real, but
 * not in the worked slice — which is why lead can time a card cycle does not.
 */
export function leadTime(issues: Issue[], now: number, days = DEFAULT_CYCLE_WINDOW): LeadTime {
  const from = now - days * DAY;
  const prevFrom = now - 2 * days * DAY;
  /* A card can be timed only with a recorded raise, start and finish, in order —
     the same filter cycle time uses, plus the raise lead time also needs. */
  const timed = (i: Issue): boolean =>
    i.status === 'done' &&
    i.createdAt != null && i.startedAt != null && i.completedAt != null &&
    i.startedAt >= i.createdAt && i.completedAt >= i.startedAt;
  const leadOf = (i: Issue) => (i.completedAt! - i.createdAt!) / DAY;
  const waitOf = (i: Issue) => (i.startedAt! - i.createdAt!) / DAY;
  const workOf = (i: Issue) => (i.completedAt! - i.startedAt!) / DAY;
  const finishedIn = (lo: number, hi: number): Issue[] =>
    issues.filter((i) => timed(i) && (i.completedAt as number) >= lo && (i.completedAt as number) < hi);

  const cur = finishedIn(from, now + DAY);
  const prev = finishedIn(prevFrom, from);
  const leadMed = Math.round(median(cur.map(leadOf)));
  /* The green locks to the Cycle time panel, so it times the SAME cards it does:
     only those that had a real work phase. A card picked straight to Done in one
     move is an instant landing, not work (hadWorkPhase), so it is out of the
     worked slice — but it stays in the lead and wait above, because the time it
     waited before being picked up is real. */
  const workMed = Math.round(median(cur.filter(hadWorkPhase).map(workOf)));
  /* THE BAR DECOMPOSES THE HERO. The working slice is the exact cycle-time
     median (the two panels lock together), and the wait is the REMAINDER of the
     lead median — so Waiting + Working add up to the headline and no reader ever
     has to subtract. Medians do not sum, so the measured raise→start median is
     NOT used for the segment (it would leave the bar short of the hero); it is
     lead − work, which order-statistic monotonicity keeps ≥ 0. The measured wait
     distribution still supplies the p90 tail, a true "worst waits" number. */
  const waitMed = Math.max(0, leadMed - workMed);
  const prevLead = prev.length >= CYCLE_MIN_SAMPLE ? Math.round(median(prev.map(leadOf))) : null;

  /* The month-trend: lead median per calendar month, oldest first, so a growing
     wait shows as a rising line before it shows as a crisis. Insights only. */
  const anchor = new Date(now);
  const months: LeadMonth[] = [];
  for (let k = LEAD_TREND_MONTHS - 1; k >= 0; k--) {
    const lo = new Date(anchor.getFullYear(), anchor.getMonth() - k, 1).getTime();
    const hi = new Date(anchor.getFullYear(), anchor.getMonth() - k + 1, 1).getTime();
    const inMonth = finishedIn(lo, hi);
    months.push({ from: lo, median: Math.round(median(inMonth.map(leadOf))), sample: inMonth.length });
  }

  return {
    days,
    sample: cur.length,
    median: leadMed,
    waitMedian: waitMed,
    waitP90: Math.round(quantile(cur.map(waitOf), 0.9)),
    workMedian: workMed,
    prevMedian: prevLead,
    delta: prevLead !== null ? leadMed - prevLead : null,
    months,
  };
}

/* ── REPEATEDLY PUSHED DATES: the cards whose plan keeps slipping ──────────── */

export interface PushedRow {
  id: string;
  ref: string;
  title: string;
  /** current − baseline in days, always positive here (two+ later pushes). */
  slipDays: number;
  /** How many times the target date has moved later. */
  pushes: number;
  assignee: string | null;
}

export interface PushedDates {
  /** Cards whose target date has moved later two or more times, while live. */
  count: number;
  /** Median slip across those cards, in days — 0 when there are none. */
  medianSlip: number;
  /** How many people own two or more of these cards. */
  ownerCount: number;
  /** The longest slip among them, so the bars can be scaled — 0 when empty. */
  maxSlip: number;
  /** Every qualifying card, worst slip first. The card slices what it shows. */
  rows: PushedRow[];
}

/**
 * THE CARDS WHOSE TARGET DATE KEEPS BEING PUSHED LATER — the Insights slippers
 * panel (migration 0125). The decision-tier version of a signal Jira leaves to a
 * paid add-on: which work keeps slipping, by how much, and who owns it.
 *
 * A card qualifies when its target date has moved LATER two or more times AND the
 * card is live (isLiveCard) — a date moved before the work starts is re-planning,
 * not slippage, the same rule the card face and the backlog token follow. Slip is
 * current minus the first-set baseline; because every qualifier has moved later,
 * that slip is positive.
 */
export function pushedDates(issues: Issue[]): PushedDates {
  const rows: PushedRow[] = [];
  for (const i of issues) {
    if (!isLiveCard(i)) continue;
    if ((i.dueDatePushes ?? 0) < PUSH_MARK_THRESHOLD) continue;
    const slip = dueSlip(i);
    if (!slip || slip.slipDays == null || slip.slipDays <= 0) continue;
    rows.push({
      id: i.id,
      ref: i.ref,
      title: i.title,
      slipDays: slip.slipDays,
      pushes: slip.pushes,
      assignee: i.assignee ?? null,
    });
  }
  rows.sort((a, b) => b.slipDays - a.slipDays || b.pushes - a.pushes);

  const byOwner = new Map<string, number>();
  for (const r of rows) if (r.assignee) byOwner.set(r.assignee, (byOwner.get(r.assignee) ?? 0) + 1);
  const ownerCount = [...byOwner.values()].filter((n) => n >= 2).length;

  return {
    count: rows.length,
    medianSlip: rows.length ? Math.round(median(rows.map((r) => r.slipDays))) : 0,
    ownerCount,
    maxSlip: rows.reduce((m, r) => Math.max(m, r.slipDays), 0),
    rows,
  };
}

/* ── CUMULATIVE FLOW: every card by stage, week by week ───────────────────── */

/**
 * A status change on one card — the only honest source for a flow diagram.
 *
 * A card's own date stamps say when it started and finished, never when it
 * entered review, so the shape of the work over time can only be rebuilt from
 * the transition log. Fetched on demand for the window the diagram needs (the
 * board read carries only a few days); see store.loadFlowMoves.
 */
export interface FlowMove {
  issueId: string;
  at: number;
  fromStatus?: IssueStatus;
  toStatus: IssueStatus;
}

/** The bands a cumulative-flow diagram stacks. Backlog folds into To Do;
 *  abandoned work (wontdo) leaves the chart rather than sitting in a band.
 *  `staging` only carries cards on boards that turned the In Staging column on,
 *  so on every other board its band is all-zeros and the chart drops it. */
export type FlowStage = 'backlog' | 'todo' | 'inprogress' | 'staging' | 'inreview' | 'done';

export interface FlowWeek {
  /** The instant this column is measured at — the end of the week, newest last. */
  at: number;
  backlog: number;
  todo: number;
  inprogress: number;
  staging: number;
  inreview: number;
  done: number;
  /** Committed work still on the board (todo + inprogress + staging + inreview) —
   *  work in progress. The BACKLOG is candidate work, not WIP (the commitment
   *  point: nothing in the holding pen has been taken on), so it is excluded. */
  wip: number;
  /** Everything arrived and not abandoned — the top edge of the stack. */
  total: number;
}

export interface CumulativeFlow {
  /** One column per week, oldest first. */
  weeks: FlowWeek[];
  /** The top edge rose faster than Done did — work is arriving faster than it
   *  is closing, so the backlog of open work is growing. */
  arrivalsOutpacing: boolean;
  /** The In Review band is wider at the end than the start — the first sign of a
   *  review bottleneck, which is the one the mock calls out by name. */
  reviewWidening: boolean;
  /** How many non-abandoned cards the diagram is drawn from. */
  sample: number;
}

/* Read wider than it is drawn: the extra weeks of moves establish each card's
   stage at the LEFT edge of the diagram, which a card's first in-window move
   would otherwise get wrong. store.loadFlowMoves reads this many weeks, and it
   must clear the widest the diagram is ever drawn — a 90-day window is ~13 weeks
   — with a couple of weeks over for that left-edge reconstruction. */
export const FLOW_WINDOW_WEEKS = 15;

/** Map any board status to a flow band; wontdo returns null — abandoned work is
 *  not "in flight" and not "done", it has left the board. Backlog is its own
 *  band now (the least-progressed stage), so the stack ties out column-for-column
 *  to the backlog sidebar: Backlog · To Do · In Progress · In Review · Done. */
function flowStageOf(status: IssueStatus): FlowStage | null {
  switch (status) {
    case 'done': return 'done';
    case 'inreview': return 'inreview';
    case 'staging': return 'staging';
    case 'inprogress': return 'inprogress';
    case 'backlog': return 'backlog';
    case 'wontdo': return null;
    default: return 'todo'; // any remaining open status is "to do, not started"
  }
}

/**
 * EVERY CARD'S STAGE, WEEK BY WEEK — the cumulative-flow diagram.
 *
 * For each week end, every card is placed in the band it was in at that instant,
 * reconstructed from the transition log: the latest move at or before the
 * instant gives the stage, the first move's `fromStatus` gives the stage it sat
 * in before any move in the window, and a card with no moves has held its current
 * stage unchanged (that is what "no move" means). A card created after the
 * instant is not on the board yet and is not counted.
 *
 * The bands stack to the total, so the top edge is arrivals and the Done band is
 * departures; the gap between them is work in progress. Two readings come out of
 * it without the reader doing the arithmetic: whether arrivals are outpacing
 * closes, and whether the review band is widening into a bottleneck.
 */
/**
 * `columns` is how many points to draw and `stepDays` how far apart they sit —
 * `(…, 10, 7)` is ten weekly columns (the default, unchanged for every existing
 * caller), `(…, 28, 1)` is twenty-eight DAILY columns for the day-level scrub.
 * The reconstruction is per-instant, so a day is read exactly like a week end.
 */
export function cumulativeFlow(
  issues: Issue[],
  moves: FlowMove[],
  now: number,
  columns = 10,
  stepDays = 7,
): CumulativeFlow {
  const byIssue = new Map<string, FlowMove[]>();
  for (const m of moves) {
    const arr = byIssue.get(m.issueId);
    if (arr) arr.push(m);
    else byIssue.set(m.issueId, [m]);
  }
  for (const arr of byIssue.values()) arr.sort((a, b) => a.at - b.at);

  const statusAt = (i: Issue, t: number): IssueStatus | null => {
    if ((i.createdAt ?? 0) > t) return null; // not raised yet
    const ms = byIssue.get(i.id);
    if (!ms || ms.length === 0) return i.status; // never moved in the window
    let s: IssueStatus | undefined = ms[0].fromStatus;
    for (const m of ms) {
      if (m.at <= t) s = m.toStatus;
      else break;
    }
    return s ?? i.status;
  };

  const out: FlowWeek[] = [];
  for (let k = columns - 1; k >= 0; k--) {
    const at = now - k * stepDays * DAY;
    const wk: FlowWeek = { at, backlog: 0, todo: 0, inprogress: 0, staging: 0, inreview: 0, done: 0, wip: 0, total: 0 };
    for (const i of issues) {
      const st = statusAt(i, at);
      if (st == null) continue;
      const band = flowStageOf(st);
      if (band == null) continue;
      wk[band]++;
      wk.total++;
      // WIP = committed work in flow. Backlog is candidate work (the holding pen),
      // never WIP — the aggregate-bucket invariant. Done has left the board.
      if (band !== 'done' && band !== 'backlog') wk.wip++;
    }
    out.push(wk);
  }

  const first = out[0];
  const last = out[out.length - 1];
  const arrivalsOutpacing = !!first && !!last && last.total - first.total > last.done - first.done;
  const reviewWidening = !!first && !!last && last.inreview > first.inreview && last.inreview >= 3;
  const sample = issues.filter((i) => flowStageOf(i.status) != null && (i.createdAt ?? 0) <= now).length;
  return { weeks: out, arrivalsOutpacing, reviewWidening, sample };
}

export type Dimension = 'area' | 'epic' | 'kind' | 'assignee' | 'responsible' | 'status';

/*
 * ORDER IS THE ORDER OF THE BUTTONS. Stage is last because it is the odd one
 * out: the other four ask WHAT the work is or WHO has it, and Stage asks how
 * far along it is. Putting it in the middle of the others makes the row read
 * as five unrelated words.
 */
export const DIMENSION_LABEL: Record<Dimension, string> = {
  area: 'Area',
  epic: 'Epic',
  kind: 'Type',
  assignee: 'Accountable',
  responsible: 'Responsible',
  status: 'Stage',
};

export interface SplitRow {
  /** What the filter must be set to in order to show exactly this row. */
  key: string;
  label: string;
  /** The row's own colour where it has one, '' where the screen should pick. */
  colour: string;
  total: number;
  /**
   * How much of the row is FIXING work, so a bar can show its own composition.
   *
   * Renamed from `bugs` by #302: it is no longer "cards spelled bug", it is
   * every type this board says counts as fixing something broken. The old name
   * would have quietly invited the old comparison back.
   */
  fixing: number;
  /** Which stages this row's work is in — what its hover card says. */
  stages: Part[];
  /** The cards themselves, so any row can open exactly them. */
  ids: string[];
}

/**
 * ONE CHART, SPLIT WHICHEVER WAY ANSWERS THE QUESTION.
 *
 * This replaces four separate panels — by stage, by epic, by area, and bug
 * share by area — which between them were most of the scrolling. They were four
 * panels because each was built when it was asked for; nothing about them
 * needed to be four things on a screen at once.
 *
 * THE EMPTY ANSWER IS ALWAYS LAST AND ALWAYS PRESENT. "No area" and "Unsorted"
 * are real answers about real work, and a chart that drops them adds up to less
 * than the board and quietly lies about the total.
 */
export function splitBy(
  issues: Issue[],
  by: Dimension,
  areas: Area[],
  profiles: Profiles = {},
  /* The board's own types — #302. Defaulted so every existing caller and every
     check keeps working, reading the four originals. */
  types: IssueType[] = ORIGINAL_TYPES
): SplitRow[] {
  const open = issues.filter((i) => !isClosed(i));
  const rows = new Map<string, SplitRow>();
  const held = new Map<string, Issue[]>();
  const add = (key: string, label: string, colour: string, i: Issue) => {
    const r = rows.get(key) ?? { key, label, colour, total: 0, fixing: 0, stages: [], ids: [] };
    r.total++;
    /*
     * ⚠️ BY WHAT IT COUNTS AS, NOT BY HOW IT IS SPELLED — #302.
     *
     * This read `i.kind === 'bug'`, so the darker part of every bar was "cards
     * spelled bug". A board calling it "Defect" saw that go to zero while the
     * chart carried on looking correct, which is the failure #302 exists to
     * prevent. Every Fixing type counts now, whatever the board calls it.
     */
    if (meansOf(i.kind, types) === 'fixing') r.fixing++;
    r.ids.push(i.id);
    rows.set(key, r);
    held.set(key, [...(held.get(key) ?? []), i]);
  };

  for (const i of open) {
    if (by === 'area') {
      const a = areas.find((x) => x.id === i.area);
      add(a ? a.id : NO_AREA, a ? a.name + (a.retiredAt ? ' (retired)' : '') : 'No area', a?.colour ?? '', i);
    } else if (by === 'epic') {
      const e = (i.epic ?? '').trim();
      add(e || NO_AREA, e || 'Unsorted', '', i);
    } else if (by === 'assignee') {
      /* Unassigned is not a gap in the data, it is the answer "nobody has
         picked this up" — and on a board where that is the biggest bar, it is
         the most useful thing the chart says. It sorts last with the other
         empty answers, not out of the total. */
      const who = (i.assignee ?? '').trim();
      /* A PERSON, NOT AN ADDRESS. The bars first went up reading
         "ui-admin-48e5f2…" truncated at twenty characters, which is a chart
         nobody can read. Every other screen says the name, so this does too. */
      add(who || NO_AREA, who ? displayName(who, profiles) : 'Nobody', '', i);
    } else if (by === 'responsible') {
      /* The Responsible (two-part assignee). Same shape as Accountable: a person
         not an address, and "Nobody" is a real answer — the work nobody is
         holding — that sorts last with the other empties, never out of the total. */
      const doer = (i.responsible ?? '').trim();
      add(doer || NO_AREA, doer ? displayName(doer, profiles) : 'Nobody', '', i);
    } else if (by === 'kind') {
      /* THE BOARD'S NAME FOR IT, not ours — a bar labelled "New feature" on a
         board that calls it "Enhancement" is the chart contradicting the card. */
      add(i.kind, labelOf(i.kind, types), '', i);
    } else {
      add(i.status, STATUS_LABELS[i.status as IssueStatus] ?? i.status, '', i);
    }
  }

  /*
   * STAGE IS SORTED BY THE WORKFLOW, NOT BY SIZE.
   *
   * Every other split answers "which is biggest", so biggest-first is the
   * order. Stage does not: it is a sequence work moves along, and sorting it by
   * count produced "Backlog, In Progress, To Do" on a board with one card in
   * each — a reading order that has to be undone in the reader's head before
   * the chart says anything. Caught by looking at it, not by a test.
   */
  for (const r of rows.values()) r.stages = byStage(held.get(r.key) ?? []);

  const all = [...rows.values()];
  if (by === 'status') {
    return all.sort((a, b) => STATUS_ORDER.indexOf(a.key as IssueStatus) - STATUS_ORDER.indexOf(b.key as IssueStatus));
  }
  const empty = (r: SplitRow) => r.key === NO_AREA;
  return all.sort(
    (a, b) => Number(empty(a)) - Number(empty(b)) || b.total - a.total || a.label.localeCompare(b.label)
  );
}

/**
 * THE AREA SPLIT, ACROSS BOARDS, BY NAME — #462.
 *
 * `splitBy`'s area branch keys by the board-local area id, so the all-boards
 * union cannot use it: another board's ids mean nothing in this board's list,
 * and everything would file under "No area". This resolves each card's area id to
 * a NAME within its OWN board — the exact move `rollUpWorkspaceAreas` makes for
 * the Portfolio Areas lens — and groups by name, so one area that runs on three
 * boards is one bar, matching the lens. The colour is the first one any board
 * gives the name, like the roll-up. A card on a board that listed no areas, or
 * with an id its board does not define, reads as "No area" — honest, not dropped,
 * and sorted last like every other empty answer.
 *
 * Keyed by name alone (no "(retired)" suffix): retirement is per-board, but a
 * cross-board row spans boards, so the plain name is what the lens shows too. The
 * fixing share uses the one `types` list the caller holds (the open board's), the
 * same approximation `splitBy` already makes for the union's other dimensions.
 */
export function splitByAreaName(
  issuesByBoard: Map<string, Issue[]>,
  areasByBoard: Map<string, Area[]>,
  types: IssueType[] = ORIGINAL_TYPES
): SplitRow[] {
  const rows = new Map<string, SplitRow>();
  const held = new Map<string, Issue[]>();
  const add = (key: string, label: string, colour: string, i: Issue) => {
    const r = rows.get(key) ?? { key, label, colour, total: 0, fixing: 0, stages: [], ids: [] };
    if (!r.colour && colour) r.colour = colour; // first board to colour the name wins
    r.total++;
    if (meansOf(i.kind, types) === 'fixing') r.fixing++;
    r.ids.push(i.id);
    rows.set(key, r);
    held.set(key, [...(held.get(key) ?? []), i]);
  };

  for (const [boardId, issues] of issuesByBoard) {
    const areas = areasByBoard.get(boardId) ?? [];
    for (const i of issues) {
      if (isClosed(i)) continue;
      const a = areas.find((x) => x.id === i.area);
      if (a) add(a.name, a.name, a.colour ?? '', i);
      else add(NO_AREA, 'No area', '', i);
    }
  }

  for (const r of rows.values()) r.stages = byStage(held.get(r.key) ?? []);

  const empty = (r: SplitRow) => r.key === NO_AREA;
  return [...rows.values()].sort(
    (a, b) => Number(empty(a)) - Number(empty(b)) || b.total - a.total || a.label.localeCompare(b.label)
  );
}
